import React, { createContext, useContext } from "react";
import { ErrorLevel, MoneyLinkException, MoneyLinkWarning } from "@/shared/utils/custom-exception";

// ============================================================================
// 型定義
// ============================================================================

/**
 * グローバルエラーハンドリングのContext型
 */
export type ErrorDialogContextType = {
  /** 現在のエラー */
  error: Error | undefined;
  /** エラーを設定する関数 */
  setError: (error: Error | undefined) => void;
};

/**
 * エラー情報を表すインターフェース
 */
export interface ErrorInfo {
  /** エラーダイアログのタイトル */
  title: string;
  /** エラーメッセージ */
  message: React.ReactNode;
  /** エラーコード */
  errorCode: number;
  /** エラータイプ */
  errorType: string;
  /** エラー発生時のコールバック関数 */
  callback: Readonly<(...args: unknown[]) => unknown>;
}

/**
 * エラー表示タイプ
 * - "none": エラーなし
 * - "dialog": ダイアログ表示（警告レベル）
 * - "page": エラーページ表示（致命的エラー）
 */
export type ErrorDisplayType = "none" | "dialog" | "page";

/**
 * Promiseから値の型を抽出するユーティリティ型
 */
export type GetPromiseValue<T> = T extends Promise<infer U> ? U : never;

// ============================================================================
// Context
// ============================================================================

const defaultContext: ErrorDialogContextType = {
  error: undefined,
  setError: () => {
    /* デフォルトは何もしない */
  },
};

/**
 * グローバルエラーハンドリング用のReact Context
 */
export const ErrorDialogContext =
  createContext<ErrorDialogContextType>(defaultContext);

// ============================================================================
// エラーハンドリングラッパー関数
// ============================================================================

/**
 * 同期関数のエラーハンドリングを行うラッパー関数
 *
 * @description
 * 同期関数をラップし、エラー発生時に自動的にエラーダイアログを表示する。
 * エラーが発生した場合は `undefined` を返す。
 * 
 * **注意**: async関数を渡した場合も、Promiseのrejectをキャッチして
 * エラーダイアログを表示する（後方互換性のため）。
 * ただし、明示的にasync関数を使う場合は `errorHandledAsync` の使用を推奨。
 *
 * @param callback - ラップされる同期関数
 * @param setError - ErrorDialogContext から取得したエラーセッター
 * @returns ラップされた同期関数（エラー時は undefined を返す）
 *
 * @example
 * const { setError } = useContext(ErrorDialogContext);
 * const safeFunction = errorHandledSync(riskyFunction, setError);
 */
export const errorHandledSync = <T extends unknown[], U>(
  callback: (...args: T) => U,
  setError: (error: Error | undefined) => void
): ((...args: T) => U | undefined) => {
  return (...args: T): U | undefined => {
    try {
      const result = callback(...args);
      
      // 戻り値がPromiseの場合は、rejectをキャッチしてエラーダイアログを表示
      // これにより、async関数をerrorHandledSyncに渡しても正しく動作する
      if (result instanceof Promise) {
        result.catch((e) => {
          if (e instanceof Error) {
            setError(e);
          }
        });
      }
      
      return result;
    } catch (e) {
      if (e instanceof Error) {
        setError(e);
      }
      return undefined;
    }
  };
};

/**
 * 非同期関数のエラーハンドリングを行うラッパー関数
 *
 * @description
 * 非同期関数をラップし、エラー発生時に自動的にエラーダイアログを表示する。
 * エラーが発生した場合は `undefined` を返す（Promiseはrejectされない）。
 *
 * @param callback - ラップされる非同期関数
 * @param setError - ErrorDialogContext から取得したエラーセッター
 * @returns ラップされた非同期関数（エラー時は undefined を返す）
 *
 * @example
 * const { setError } = useContext(ErrorDialogContext);
 * const safeFetchData = errorHandledAsync(fetchData, setError);
 */
export const errorHandledAsync = <
  T extends unknown[],
  U extends Promise<unknown>,
  K extends GetPromiseValue<U>
>(
  callback: (...args: T) => Promise<K>,
  setError: (error: Error | undefined) => void
): ((...args: T) => Promise<K | void>) => {
  return async (...args: T): Promise<K | void> => {
    return callback(...args).catch((e) => {
      if (e instanceof Error) {
        setError(e);
      }
      return undefined;
    });
  };
};

// ============================================================================
// ユーティリティ関数
// ============================================================================

/**
 * エラーインスタンスからエラー情報を抽出する
 *
 * @description
 * MoneyLinkException の場合は詳細なエラー情報を抽出し、
 * それ以外の場合はデフォルト値を使用する。
 *
 * @param error - エラーインスタンス
 * @returns エラー情報オブジェクト
 */
export const extractErrorInfo = (error: Error | undefined): ErrorInfo => {
  const defaults: ErrorInfo = {
    title: "エラーが発生しました",
    message: "システムエラーが発生しました",
    errorCode: 0,
    errorType: "unknown_error_type",
    callback: () => {
      /* デフォルトは何もしない */
    },
  };

  // MoneyLinkWarning の場合
  if (error instanceof MoneyLinkWarning) {
    return {
      title: error.title,
      message: error.messageBody,
      errorCode: 0,
      errorType: "warning",
      callback: error.callback,
    };
  }

  if (!(error instanceof MoneyLinkException)) {
    return {
      ...defaults,
      message: error?.message || defaults.message,
    };
  }

  const { props } = error.error;

  return {
    title: props.messageTitle || defaults.title,
    message: props.messageBody || defaults.message,
    errorCode: props.code,
    errorType: props.reason || defaults.errorType,
    callback: error.callback,
  };
};

// ============================================================================
// 表示タイプ判定
// ============================================================================

/**
 * エラーの表示タイプを判定する
 *
 * @description
 * エラーの種類・レベルに応じて、表示方法を決定する。
 * - 警告レベル: ダイアログ表示（ユーザーは操作を続行可能）
 * - 致命的エラー: エラーページ表示（操作不可）
 * 
 * 基本方針：
 * - APIエラーを含むほとんどのエラーはダイアログ表示（モーダルを閉じずにエラー表示し再実行可能）
 * - セッション切れなど致命的なエラーのみページ表示
 *
 * @param error - エラーインスタンス
 * @returns エラー表示タイプ
 */
export const getErrorDisplayType = (error: Error | undefined): ErrorDisplayType => {
  if (!error) {
    return "none";
  }

  // MoneyLinkWarning の場合は常にダイアログ
  if (error instanceof MoneyLinkWarning) {
    return "dialog";
  }

  // MoneyLinkException の場合はレベルを確認
  if (error instanceof MoneyLinkException) {
    const level = error.error.props.level;
    const reason = error.error.props.reason;

    // エラーレベルが Error かつ致命的なエラーの場合のみページ表示
    // （セッション切れ等、復帰不可能なエラー）
    if (level === ErrorLevel.Error && isFatalError(reason)) {
      return "page";
    }

    // それ以外のMoneyLinkExceptionはすべてダイアログ表示
    // （モーダルを閉じずにエラー表示し、再実行可能にする）
    return "dialog";
  }

  // MoneyLinkException 以外の Error もダイアログ表示
  // （ネットワークエラー等、ユーザーが再試行可能なケースが多い）
  return "dialog";
};

/**
 * 致命的なエラー（ページ表示が必要なエラー）かどうかを判定する
 * 
 * @description
 * 以下のようなエラーは致命的とみなし、ページ表示にする：
 * - セッション切れ
 * - システムの根本的な障害
 * 
 * @param reason - エラー理由
 * @returns true: 致命的エラー, false: 非致命的エラー
 */
const isFatalError = (reason: string): boolean => {
  const fatalReasons = [
    "client_destroyed",
    "client_invalid_response",
    "client_server_redirect_to_error_page",
  ];
  return fatalReasons.includes(reason);
};

// ============================================================================
// カスタムフック
// ============================================================================

/**
 * ErrorDialogContext を使用するためのカスタムフック
 *
 * @returns ErrorDialogContext の値と、表示タイプ・エラー情報
 */
export const useErrorContext = () => {
  const context = useContext(ErrorDialogContext);
  return {
    ...context,
    displayType: getErrorDisplayType(context.error),
    errorInfo: extractErrorInfo(context.error),
  };
};
